import { AlertDialog } from 'radix-ui';
import { type ReactNode, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';

// Confirm dialogs (S30 «Conferma semplice», «Conferma distruttiva con motivo»):
// `await confirm({...})` resolves with the answer; a destructive confirm can
// require a reason (07 S08: release to manual, close manually, withdraw).

export interface ConfirmOptions {
  title: ReactNode;
  body?: ReactNode;
  confirmLabel: ReactNode;
  destructive?: boolean;
  /** Ask for a reason; the confirm button stays disabled until it is written. */
  reason?: { placeholder: string };
}
export interface ConfirmResult {
  ok: boolean;
  reason?: string;
}

interface Pending extends ConfirmOptions {
  resolve(result: ConfirmResult): void;
}
let pending: Pending | null = null;
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

export function confirm(options: ConfirmOptions): Promise<ConfirmResult> {
  pending?.resolve({ ok: false });
  return new Promise((resolve) => {
    pending = { ...options, resolve };
    emit();
  });
}

function settle(result: ConfirmResult) {
  const current = pending;
  pending = null;
  emit();
  current?.resolve(result);
}

/** The dialog body, also used on the component sheet without a portal. */
export function ConfirmCard({
  options,
  reason,
  onReason,
  onCancel,
  onConfirm,
  asDialog = false,
}: {
  options: ConfirmOptions;
  reason: string;
  onReason(value: string): void;
  onCancel(): void;
  onConfirm(): void;
  asDialog?: boolean;
}) {
  const { t } = useTranslation();
  const blocked = options.reason !== undefined && reason.trim().length === 0;
  const title = <strong>{options.title}</strong>;
  const body = options.body ? <div className="m2">{options.body}</div> : null;
  return (
    <div className="dlg">
      <div
        style={{
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: options.reason ? 8 : 4,
        }}
      >
        {asDialog ? <AlertDialog.Title asChild>{title}</AlertDialog.Title> : title}
        {body && asDialog ? <AlertDialog.Description asChild>{body}</AlertDialog.Description> : body}
        {options.reason ? (
          <textarea
            className="inp"
            style={{ height: 60 }}
            placeholder={options.reason.placeholder}
            aria-label={options.reason.placeholder}
            value={reason}
            onChange={(e) => onReason(e.target.value)}
          />
        ) : null}
      </div>
      <div className="dlg-foot">
        <Button onClick={onCancel}>{t('actions.cancel')}</Button>
        <Button
          variant={options.destructive ? 'danger' : 'primary'}
          className={blocked ? 'dis' : undefined}
          aria-disabled={blocked}
          onClick={() => {
            if (!blocked) onConfirm();
          }}
        >
          {options.confirmLabel}
        </Button>
      </div>
    </div>
  );
}

export function ConfirmHost() {
  const current = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => pending,
  );
  const [reason, setReason] = useState('');
  return (
    <AlertDialog.Root
      open={current !== null}
      onOpenChange={(open) => {
        if (!open) {
          setReason('');
          settle({ ok: false });
        }
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="scrim" />
        <AlertDialog.Content
          className="pl fixed top-1/2 left-1/2 w-[min(440px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 bg-transparent"
          style={{ zIndex: 'var(--z-dialog)', background: 'transparent' }}
        >
          {current ? (
            <ConfirmCard
              asDialog
              options={current}
              reason={reason}
              onReason={setReason}
              onCancel={() => {
                setReason('');
                settle({ ok: false });
              }}
              onConfirm={() => {
                const r = reason.trim();
                setReason('');
                settle(current.reason ? { ok: true, reason: r } : { ok: true });
              }}
            />
          ) : null}
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
